
import Vue from 'vue';
import helper from '../../../utils/helper';

export default Vue.extend<any, any, any, any>({
  name: 'TextInputV2',
  props: {
    /*
     * This is the value
     */
    value: {
      type: [Number, String],
      default: ''
    },
    /*
     * Set id on input
     */
    id: {
      type: [Number, String],
      default: ''
    },
    /*
     * Set input placeholder
     */
    placeholder: {
      type: String,
      default: 'Placeholder'
    },
    /*
     * Set input on error state
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Set input on success state
     */
    success: {
      type: Boolean,
      default: false
    },
    /**
     * Set maximum length for input value (for all types)
     */
    maxLength: {
      type: Number,
      default: Number.POSITIVE_INFINITY
    },
    /**
     * Set minimum length for input value (for all types)
     */
    minLength: {
      type: Number,
      default: 0
    },
    /**
     * Set exact length for input value (for all types)
     */
    exactLength: {
      type: Number
    },
    /**
     * Input format (example: currency, decimals)
     */
    format: {
      type: String,
      default: ''
    },
    /**
     * To put label on bottom of the input
     */
    bottomLabel: {
      type: String,
      default: ''
    },
    /**
     * Pass custom regex
     */
    customRegex: {
      type: String,
      default: ''
    },
    /**
     * Set separator for currency input
     */
    separator: {
      type: String,
      default: ','
    },
    /**
     * Set input on required
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Make the input prevent to paste
     */
    pasteInput: {
      type: Boolean,
      default: true
    },
    /**
     * Make the input text auto capitalize
     */
    capitalize: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      focus: false,
      emailInput: ''
    };
  },
  created() {
    let state = true;
    if (this.required) {
      state = state && this.value && this.value.length > 0;
    }
    if (this.format === 'email' && this.value) {
      this.emailInput = this.value;
      state = state && !this.isNotEmail;
    }
    this.$emit('inputState', state);
  },
  computed: {
    containerClasses() {
      return {
        error: this.error || this.isNotEmail,
        success: this.success,
        focus: this.focus,
        disabled: this.$attrs.disabled === true,
        capitalize: this.capitalize
      };
    },
    bottomLabelStyle() {
      return this.error ? `color: red` : this.success ? `color: #0ec376` : '';
    },
    isNotEmail() {
      return this.format === 'email' && !!this.emailInput && !helper.isEmail(this.emailInput);
    },
    isNumberInput() {
      return this.$attrs.type === 'number' || this.format === 'currency';
    },
    modifiedVal() {
      const stringValue = String(this.value);
      return this.format === 'currency' ? this.currencyFormat(stringValue.replace(/[.,\s]/g, '')) : this.value;
    }
  },
  methods: {
    currencyFormat(val: String) {
      const parts = val.toString().split('.');
      // @ts-ignore
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.separator);
      return parts.join(',');
    },
    handleFocus(e: Event) {
      this.focus = true;
      /**
       * Method to focus
       * @event focus
       * @type {function}
       */
      this.$emit('focus', e);
    },
    validationResult(valueData: String) {
      let validationState = true;
      if (this.required) {
        validationState = !!valueData && valueData.length > 0;
      }
      if (this.format === 'email') {
        validationState = validationState && !this.isNotEmail;
      }
      if (this.minLength > 0) {
        validationState =
          (validationState && valueData.length >= this.minLength) || (valueData.length === 0 && !this.required);
      }
      if (this.exactLength) {
        validationState =
          validationState && (valueData.length === this.exactLength || (valueData.length === 0 && !this.required));
      }
      return validationState;
    },
    handleBlur(e: Event) {
      this.focus = false;
      const valueData = (e.target as HTMLInputElement).value;
      /**
       * Returns validation on blur. This method works in these conditions:
       * 1. if props required is given. Meaning if the value is null, inputState = false.
       * 2. the input length doesn't fit minLength/exactLength props given. Example, if minLength is 5 and value length is 3; inputState = false
       * 3. Email format.
       * @event inputState
       * @type {function}
       */
      this.$emit('inputState', this.validationResult(valueData));
      /**
       * When on blur
       * @event blur
       * @type {function}
       */
      this.$emit('blur', e);
    },
    handleInput(e: Event) {
      let valueData = (e.target as HTMLInputElement).value;
      if (this.format === 'currency') {
        const stringValue = String(valueData);
        valueData = this.currencyFormat(stringValue.replace(/[.,\s]/g, ''));
      }
      if (this.capitalize) {
        valueData = valueData.toUpperCase();
      }
      /**
       * Input value
       * @event input
       * @type {function}
       */
      this.$emit('input', valueData);
    },
    handleChange(e: Event) {
      let valueData = (e.target as HTMLInputElement).value;
      if (this.format === 'email') this.emailInput = valueData;
      if (
        valueData.length > this.maxLength ||
        (!helper.isDecimal(valueData) && this.format === 'decimals') ||
        (!helper.isAlphabetic(valueData) && this.format === 'text') ||
        (this.format === 'currency' && !helper.isNumeric(valueData))
      ) {
        this.$emit('input', '');
      } else {
        /**
         * Input value
         * @event input
         * @type {function}
         */
        this.$emit('input', valueData);
      }
      /**
       * Method to change
       * @event change
       * @type {function}
       */
      this.$emit('change', e);
    },
    handleKeyPress(e: any) {
      const valueData = (e.target as HTMLInputElement).value;
      const stringKey = String(e.key);
      const stringValue = String(valueData);
      if (valueData.length >= this.maxLength || this.isWrongInput(stringKey, stringValue)) {
        e.preventDefault();
      }
      const completedValue = stringKey + stringValue;
      /**
       * Returns validation on keypress. This method works in these conditions:
       * 1. if props required is given. Meaning if the value is null, changeState = false.
       * 2. the input length doesn't fit minLength/exactLength props given. Example, if minLength is 5 and value length is 3; changeState = false
       * 3. Email format.
       * @event changeState
       * @type {function}
       */
      this.$emit('changeState', this.validationResult(completedValue));
      /**
       * Method to keypress
       * @event keypress
       * @type {function}
       */
      this.$emit('keypress', e);
    },
    handlePaste(e: Event) {
      if (!this.pasteInput) {
        e.preventDefault();
        return;
      }
    },
    isWrongInput(val: any, valueData: any) {
      const numbers = /[0-9]/;
      const regex = RegExp(this.customRegex);
      if (
        (valueData.length >= this.exactLength && this.exactLength) ||
        (!!this.customRegex && !regex.test(val)) ||
        (!numbers.test(val) && this.isNumberInput) ||
        (!helper.isAlphabetic(val) && this.format === 'text') ||
        (!helper.isDecimal(val) && this.format === 'decimals') ||
        (this.format === 'currency' && val === '0' && valueData === '0')
      ) {
        return true;
      }
      return false;
    }
  }
});
